/********************************************************************************
 * Copyright (c) 2026 EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

import {
   type ContainerConfiguration,
   TYPES,
   configureActionHandler,
   DiamondNodeView,
   GCompartment,
   GLabel,
   GLabelView,
   GNode,
   RoundedCornerNodeView,
   StructureCompartmentView,
   ToolPalette,
   configureModelElement,
   editLabelFeature,
   selectFeature,
   RankedSelectMouseListener,
   initializeDiagramContainer
} from '@eclipse-glsp/client';
import { renderFrameworkMessage } from '@hydranium/protocol';
import { HydraniumGEdge, configureHydraniumModelElements } from '@hydranium/glsp-client';
import { type Container, ContainerModule } from 'inversify';
import {
   PROCESS_BRANCH_EDGE_TYPE,
   PROCESS_EDITABLE_LABEL_TYPE,
   PROCESS_EFFECT_COMPARTMENT_TYPE,
   PROCESS_EFFECT_TYPE,
   PROCESS_GATEWAY_NODE_TYPE,
   PROCESS_TASK_NODE_TYPE,
   PROCESS_TRANSITION_EDGE_TYPE,
   type ProcessElementType
} from '@hydranium/example-order-flow-protocol';
import { OrderFlowProcessEdit, OPEN_PROCESS_EDIT, ProcessEditMessageRenderer } from './order-flow-process-edit';
import { ProcessGatewayNode } from './order-flow-process-model';
import { OrderFlowProcessSelectMouseListener } from './order-flow-process-select';
import { OrderFlowProcessToolPalette } from './order-flow-process-tool-palette';
import { ProcessEdgeView } from './order-flow-process-views';

/**
 * The model class and the view a `.process` element type renders through.
 *
 * Both slots are spelled as the parameter types of `configureModelElement`
 * itself, so the table cannot drift from the function it feeds.
 */
export interface ProcessElementRegistration {
   readonly model: Parameters<typeof configureModelElement>[2];
   readonly view: Parameters<typeof configureModelElement>[3];
   readonly options?: Parameters<typeof configureModelElement>[4];
}

/**
 * How each element type the server stamps is realised on the client.
 *
 * Keyed by {@link ProcessElementType}, so a new server element type added to
 * `PROCESS_ELEMENT_TYPES` fails to compile until it is realised here. That
 * closes the half of the client/server contract a test cannot: an id that
 * matches but has no registration still renders nothing, because sprotty falls
 * back to a featureless generic element plus `MissingView`.
 *
 * GLSP's defaults cover the graph root. Name and branch labels enable editing;
 * effect labels keep their own registration without that feature.
 */
export const PROCESS_ELEMENT_REGISTRATIONS: Readonly<Record<ProcessElementType, ProcessElementRegistration>> = {
   [PROCESS_TASK_NODE_TYPE]: { model: GNode, view: RoundedCornerNodeView },
   // A gateway is a branch point, so it gets the shape the notation expects
   // rather than a second rounded rectangle — and the MODEL class that goes with
   // that shape, because the anchor an edge aims at comes from the model and not
   // from the view. See `ProcessGatewayNode`.
   [PROCESS_GATEWAY_NODE_TYPE]: { model: ProcessGatewayNode, view: DiamondNodeView },
   [PROCESS_EFFECT_COMPARTMENT_TYPE]: { model: GCompartment, view: StructureCompartmentView },
   [PROCESS_EDITABLE_LABEL_TYPE]: { model: GLabel, view: GLabelView, options: { enable: [editLabelFeature] } },
   [PROCESS_EFFECT_TYPE]: { model: GLabel, view: GLabelView, options: { enable: [selectFeature] } },
   // Both connection types are directed, so both are drawn with an arrowhead;
   // what tells them apart is the stylesheet, keyed on the `transition` /
   // `branch` subtype class. The framework's edge rather than `GEdge`, so a
   // branch label has finite bounds and fit-to-screen keeps the canvas.
   [PROCESS_TRANSITION_EDGE_TYPE]: { model: HydraniumGEdge, view: ProcessEdgeView },
   [PROCESS_BRANCH_EDGE_TYPE]: { model: HydraniumGEdge, view: ProcessEdgeView }
};

/**
 * The `.process` diagram definition: GLSP's default model elements, the edge
 * being the framework's, plus one model/view registration per element type
 * the server stamps.
 *
 * **Host-agnostic on purpose.** It binds nothing a host owns — no logger, no
 * connector, no message service — so the same module is loaded by the Theia
 * shell (through `@hydranium/glsp-client-theia`), by the VS Code shell (on
 * GLSP's `vscode-integration`) and by a browser app. Each host contributes its
 * own `ContainerModule` alongside this one; see
 * {@link initializeOrderFlowProcessDiagramContainer}.
 *
 * The tool palette needs no ENTRIES here: GLSP drives it from the server's
 * `shapeTypeHints` / `edgeTypeHints`, which is why the palette and the
 * operation handlers backing it live in `order-flow-server`. Its CLASS is
 * rebound, for the read-only reason {@link OrderFlowProcessToolPalette} gives.
 *
 * A `rebind` of the class rather than of each token it serves: upstream binds
 * `ToolPalette` to itself and then routes four other tokens — the UI-extension
 * list, the diagram startup and three action handlers — to that one service, so
 * replacing the class replaces every one of them, while rebinding the tokens
 * would leave the original palette instance still built and still listening.
 */
export const orderFlowProcessDiagramModule = new ContainerModule((bind, unbind, isBound, rebind) => {
   const context = { bind, unbind, isBound, rebind };
   configureHydraniumModelElements(context);
   if (!isBound(ProcessEditMessageRenderer)) {
      bind<ProcessEditMessageRenderer>(ProcessEditMessageRenderer).toConstantValue(message => renderFrameworkMessage(message));
   }
   bind(OrderFlowProcessEdit).toSelf().inSingletonScope();
   bind(TYPES.IContextMenuItemProvider).toService(OrderFlowProcessEdit);
   configureActionHandler(context, OPEN_PROCESS_EDIT, OrderFlowProcessEdit);
   for (const [elementTypeId, registration] of Object.entries(PROCESS_ELEMENT_REGISTRATIONS)) {
      configureModelElement(context, elementTypeId, registration.model, registration.view, registration.options);
   }
   rebind(RankedSelectMouseListener).to(OrderFlowProcessSelectMouseListener).inSingletonScope();
   rebind(ToolPalette).to(OrderFlowProcessToolPalette).inSingletonScope();
});

/**
 * Build a `.process` diagram container. The host's own modules go through
 * `containerConfiguration`; the diagram definition is appended LAST.
 *
 * **A host replaces a model class or view with `overrideModelElement`, or
 * `configureView` with `isOverride` for the view alone, from a module applied
 * to the container AFTER this function returns.** A second plain registration
 * of an element type id is no replacement: GLSP's model and view registries
 * give the id to whichever registration came last, with a console message,
 * and the diagram definition is appended last. An override
 * before the definition throws, since its id is not registered yet.
 */
export function initializeOrderFlowProcessDiagramContainer(
   container: Container,
   ...containerConfiguration: ContainerConfiguration
): Container {
   return initializeDiagramContainer(container, ...containerConfiguration, orderFlowProcessDiagramModule);
}
